import React, { useRef, useEffect } from 'react';

/**
 * Анимированный фон «звёзды + паутинка» (как на загрузке), на весь экран.
 * Fixed, pointer-events:none, z-index:-1 — рисуется за контентом, поверх фона body.
 * density — множитель плотности; topBias — насколько больше звёзд сверху (1 = равномерно).
 */
export default function StarField({ density = 1, topBias = 1.4, zIndex = -1 }) {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let W, H, pts = [], raf;

    const size = () => {
      W = cv.width = window.innerWidth * DPR;
      H = cv.height = window.innerHeight * DPR;
      cv.style.width = window.innerWidth + 'px';
      cv.style.height = window.innerHeight + 'px';
    };
    const build = () => {
      const N = Math.max(20, Math.min(52, Math.floor(window.innerWidth * window.innerHeight / 16000 * density)));
      pts = [];
      for (let i = 0; i < N; i++) {
        pts.push({
          x: Math.random() * W,
          y: Math.pow(Math.random(), topBias) * H,           // больше звёзд сверху
          vx: (Math.random() - 0.5) * 0.18 * DPR,             // медленно
          vy: (Math.random() - 0.5) * 0.18 * DPR,
          r: (Math.random() * 1.5 + 0.7) * DPR,
        });
      }
    };
    size(); build();
    const onResize = () => { size(); build(); };
    window.addEventListener('resize', onResize);

    const MAXD = 150 * DPR;
    const step = () => {
      ctx.clearRect(0, 0, W, H);

      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        a.x += a.vx; a.y += a.vy;
        if (a.x < 0 || a.x > W) a.vx *= -1;
        if (a.y < 0 || a.y > H) a.vy *= -1;
      }

      // паутинка
      ctx.lineWidth = DPR;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i], b = pts[j];
          const dx = a.x - b.x, dy = a.y - b.y, d = Math.sqrt(dx * dx + dy * dy);
          if (d < MAXD) {
            ctx.strokeStyle = 'rgba(96,189,255,' + ((1 - d / MAXD) * 0.16) + ')';
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }

      // звёзды
      ctx.shadowBlur = 8 * DPR;
      ctx.shadowColor = 'rgba(96,189,255,0.85)';
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        ctx.beginPath(); ctx.arc(a.x, a.y, a.r, 0, 7);
        ctx.fillStyle = 'rgba(150,210,255,0.8)';
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, [density, topBias]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex }}
    />
  );
}
